import { z } from 'zod';
import { UuidSchema, IsoDateTimeSchema } from './common.js';
export const BROWSER_RECORDING_LIMITS = {
    partBytes: 8 * 1024 * 1024,
    totalBytes: 256 * 1024 * 1024,
    pendingBytes: 64 * 1024 * 1024,
    tokenLifetimeSeconds: 6 * 60 * 60,
};
export const BrowserRecordingContentTypeSchema = z.enum([
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
]);
export const BrowserRecordingCapabilitySchema = z.object({
    sessionId: UuidSchema,
    captureId: UuidSchema,
    token: z.string().min(32).max(128),
    expiresAt: IsoDateTimeSchema,
    partBytes: z.number().int().positive(),
    totalBytes: z.number().int().positive(),
    pendingBytes: z.number().int().positive(),
});
export const BrowserRecordingManifestSchema = z
    .object({
    version: z.literal(1),
    partCount: z.number().int().min(1).max(10000),
    totalBytes: z.number().int().positive().max(BROWSER_RECORDING_LIMITS.totalBytes),
    contentType: BrowserRecordingContentTypeSchema,
    startedAt: IsoDateTimeSchema,
    durationSeconds: z.number().positive().max(21600),
    connectedAtSeconds: z.number().nonnegative().nullable(),
    sampleRateHz: z.number().int().min(8000).max(192000),
    channelRoles: z.tuple([z.literal('participant'), z.literal('interviewer')]),
    participantTrackAttached: z.literal(true),
    interviewerTrackAttached: z.literal(true),
    trackReplacements: z.number().int().nonnegative(),
    pausedSeconds: z.number().nonnegative(),
    finalizedNormally: z.literal(true),
})
    .strict()
    .refine((value) => (value.connectedAtSeconds === null || value.connectedAtSeconds <= value.durationSeconds) &&
    value.pausedSeconds <= value.durationSeconds, { message: 'Recording timing exceeds duration' });
export const BrowserRecordingStatusSchema = z.enum([
    'recording',
    'uploaded',
    'validating',
    'ready',
    'incomplete',
    'failed',
    'expired',
]);
export const BrowserRecordingPartSchema = z.object({
    sequence: z.number().int().min(0).max(9999),
    sizeBytes: z.number().int().positive().max(BROWSER_RECORDING_LIMITS.partBytes),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export const BrowserRecordingStatusResponseSchema = z.object({
    captureId: UuidSchema,
    sessionId: UuidSchema,
    status: BrowserRecordingStatusSchema,
    parts: z.array(BrowserRecordingPartSchema),
    error: z.string().nullable(),
    mediaAssetId: UuidSchema.nullable(),
});
export const BrowserRecordingClaimSchema = z.object({
    captureId: UuidSchema,
    sessionId: UuidSchema,
    leaseToken: UuidSchema,
    manifest: BrowserRecordingManifestSchema,
    parts: z.array(BrowserRecordingPartSchema.extend({ url: z.string().url() })),
});
export const BrowserRecordingClaimsSchema = z.object({
    items: z.array(BrowserRecordingClaimSchema),
});
export const BrowserRecordingTransitionSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('validating'), leaseToken: UuidSchema }),
    z.object({ status: z.literal('uploaded'), leaseToken: UuidSchema, error: z.string().max(500) }),
    z.object({ status: z.literal('failed'), leaseToken: UuidSchema, error: z.string().max(500) }),
    z.object({
        status: z.literal('ready'),
        leaseToken: UuidSchema,
        mediaAssetId: UuidSchema,
    }),
]);
export const RecordingDerivativeClaimRequestSchema = z.object({
    sourceMediaAssetId: UuidSchema,
    kind: z.enum(['compression', 'waveform']),
    variant: z.string().min(1).max(80),
});
export const RecordingDerivativeClaimResponseSchema = z.object({
    // Optional while producers and consumers roll out independently.
    outcome: z.enum(['claimed', 'processing', 'completed', 'exhausted']).optional(),
    leaseToken: UuidSchema.nullable(),
});
export const RecordingDerivativeTransitionSchema = z.object({
    ...RecordingDerivativeClaimRequestSchema.shape,
    leaseToken: UuidSchema,
    status: z.enum(['processing', 'completed', 'failed']),
    error: z.string().max(500).optional(),
});
//# sourceMappingURL=BrowserRecording.js.map